import styled from "@emotion/styled"
import { Divider, Typography } from "@mui/material"
import { useLocale, useTranslations } from "next-intl"
import { transitionTime, useIsDesktop } from "ui"
import { sharedColors } from "ui/theme/colors"
import { delayTime, transitionEase } from "ui/transitions/utils"
import { FormObject } from "./Hero"
import HopOnWidget from "./HopOnWidget"

const Layout = styled.div<{ media?: boolean; locale: string }>`
  z-index: 3;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: ${(p) => (p.locale === "ru" ? "flex-end" : "center")};
  gap: 16px;
  padding-top: ${({ media }) => (media ? "140px" : "0")};
  width: 100%;
  margin: auto;

  ${({ theme }) => theme.breakpoints.up("sm")} {
    margin: unset;
    width: fit-content;
    margin-inline-start: auto;
    padding: 0;
    gap: 40px;
  }
`
const TitleWrapper = styled.div<{ $isRtl?: boolean }>`
  opacity: 0;
  animation: ${({ $isRtl }) =>
    $isRtl
      ? `slideInRtlTitle ${transitionTime}s ${transitionEase} forwards`
      : `slideInLtrTitle ${transitionTime}s ${transitionEase} forwards`};
  animation-delay: ${delayTime}s;

  @keyframes slideInLtrTitle {
    from {
      transform: translateX(-100vw);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  @keyframes slideInRtlTitle {
    from {
      transform: translateX(-100vw);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  /* On desktop, animate from bottom to top regardless of direction */
  ${({ theme }) => theme.breakpoints.up("md")} {
    animation: slideUpTitle ${transitionTime}s ${transitionEase} forwards;
    animation-delay: ${delayTime}s;
    @keyframes slideUpTitle {
      from {
        transform: translateY(100vh);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }
  }
`

const FormLayout = styled.div`
  width: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  border-radius: 12px;
  box-shadow: 0px 12px 25px 0px #26303d52;
  background-color: white;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 400px;
  }
  label {
    font-size: 16px;
  }
  opacity: 0;
  animation: slideUp ${transitionTime}s ${transitionEase} forwards;
  animation-delay: ${delayTime}s;
  @keyframes slideUp {
    from {
      transform: translateY(100vh);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledDivider = styled(Divider)`
  width: 296px;
  height: 1.5px;
  background-color: ${sharedColors.mediumGrayLight};
  align-self: center;
`

const StyledTypography = styled(Typography)`
  p {
    margin: 0;
  }
`
type Props = {
  formObject: FormObject
  platform?: "desktop" | "mobile"
}

export default function Form({ formObject, platform }: Props) {
  const t = useTranslations("Homepage")
  const isDesktop = useIsDesktop()
  // eslint-disable-next-line @typescript-eslint/no-var-requires

  const locale = useLocale()
  const isRtl = ["he", "ar"].includes(locale)

  if (platform === "mobile" && isDesktop) {
    return null
  }
  if (platform === "desktop" && !isDesktop) {
    return null
  }
  return (
    <Layout locale={locale}>
      <TitleWrapper $isRtl={isRtl}>
        <Typography
          variant="h1"
          color="white"
          dangerouslySetInnerHTML={{ __html: formObject.title || "" }}
          sx={{
            margin: 0,
            "& p": { margin: 0 },
            fontSize: { xs: "32px", md: "48px", fontWeight: 400 },
            lineHeight: { xs: "34px", md: "48px", fontWeight: 400 },
          }}
        />
      </TitleWrapper>
      <FormLayout>
        <HopOnWidget formObject={formObject} />
      </FormLayout>
    </Layout>
  )
}
